import type { User } from '@gm-commercial/profile-model';
import type { UserTabType } from '@gm-commercial/profile-model/src/models/user.model';
import type { ProfileResponse } from '@gm-commercial/rtk-clients/users/types';
import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
} from '@reduxjs/toolkit';
import merge from 'deepmerge';
import { PURGE } from 'redux-persist';

import type { RootState } from '~/app/rootReducer';
import {
  API_VERSION_DEFAULTS,
  CS_CONTINUATION_TOKEN_HEADER_NAME,
  DEFAULT_API_CONFIG,
} from '~/common/apis/api.constants';
import type { RouteParams } from '~/common/configs/route.config';
import {
  type ApiResponse,
  BDRequestStatus,
  BDRequestType,
  type OperationStatus,
} from '~/common/models/apis/apiResponse.model';
import {
  type ContinuationCache,
  type FormPayload,
  type ListViewSession,
  type NonNullablePick,
  type PagedResult,
  type PagedResultWithErrors,
  type PaginationContinuationInfo,
  type PaginationInfo,
  type SearchCriteria,
  SeverityLevel,
} from '~/common/models/common.model';
import { BDAppErrorType, BDError } from '~/common/models/error.model';
import type {
  InviteUsersRequestPayload,
  InviteUsersResponsePayload,
} from '~/common/models/inviteFleetUserForm.model';
import {
  type UserFormFields,
  type UserListFilterCriteria,
  UserListFilterCriteriaKeys,
  type UserSearchFilterInfo,
} from '~/common/models/user.model';
import {
  addAcceptLanguageHeader,
  addHeader,
  addOrganizationIdHeader,
  hasApiResult,
} from '~/common/utils/apis/api.utils';
import {
  makeDeletePayloadCreator,
  makeGetPayloadCreator,
  makePostPayloadCreator,
  makePutPayloadCreator,
  makeThunk,
} from '~/common/utils/store/thunk.helper';
import { buildUserListQueryParams } from '~/features/users/utils/userList.utils';
import {
  mapApiResponseToUser,
  mapUserFormToApiRequest,
  mapUserRoleAndInvite,
  readProfileResponse,
} from './list/mappers/users.mappers';

/**
 * Every profile endpoint answers with the same envelope, so the three thunks that return a user
 * share one adapter instead of each re-deriving the mapping from an untyped response.
 */
const profileResponseAdapter = (response: unknown): ApiResponse<User> => ({
  ...(response as Omit<ApiResponse<User>, 'result'>),
  result: mapApiResponseToUser(readProfileResponse(response)),
});

export interface UserListSession
  extends Record<keyof User, string>,
    ListViewSession {
  operationStatus: OperationStatus;
  activeUserTab?: UserTabType;
  continuationCache?: ContinuationCache<User[]>;
  filterCriteria?: UserListFilterCriteria;
  searchCriteria?: SearchCriteria;
}

enum UsersActionType {
  GET_USERS = 'users/fetchUsers',
  GET_USER_LIST = 'users/fetchUserList',
}

// If views are mutually exclusive (e.g. Account User List View and Advisor User List View)
// only 1 configuration is needed for both views
export enum UserSessionViewType {
  USER_LIST_VIEW = 'User List View',
  ACCOUNT_DETAIL_VIEW = 'Account Detail View',
  ADVISOR_DETAIL_VIEW = 'Advisor Detail View',
}

/**
 * UserSessionConfigType represent an object where session is scoped by the view and entity id
 *
 * @interface UserSessionConfigType
 * @property {UserSessionViewType} key The view that we are storing sessions.
 * @property {string} id The id of the entity you are viewing.
 */

export interface UserSessionConfigType {
  [UserSessionViewType.USER_LIST_VIEW]?: {
    [id: string]: Partial<UserListSession> | undefined;
  };
  [UserSessionViewType.ACCOUNT_DETAIL_VIEW]?: {
    [id: string]: Partial<UserListSession> | undefined;
  };
  [UserSessionViewType.ADVISOR_DETAIL_VIEW]?: {
    [id: string]: Partial<UserListSession> | undefined;
  };
}

export type UserRouteParams = Pick<
  RouteParams,
  'usersId' | 'organizationsId' | 'fleetsId'
>;

export type UserParams = UserRouteParams & { sessionId?: string };
export type UserListParams = UserRouteParams &
  PaginationContinuationInfo &
  UserSearchFilterInfo &
  PaginationInfo & { sessionId?: string };

export type AddUserFormPayload = FormPayload<
  NonNullablePick<UserFormFields, 'email' | 'role' | 'firstName' | 'lastName'> &
    Pick<UserFormFields, 'fleet' | 'phoneNumber'> & {
      status: null;
    },
  'organizationsId'
>;
export type UpdateUserFormPayload = FormPayload<
  NonNullablePick<
    UserFormFields,
    'role' | 'status' | 'firstName' | 'lastName'
  > &
    Pick<UserFormFields, 'fleet' | 'phoneNumber' | 'locale' | 'timeZone'> & {
      email: null;
    },
  'usersId',
  'organizationsId'
>;

export type ActivateUserPayload = {
  formFields: {
    urls: { termsAndConditions: string; privacyPolicy: string };
    accepted: boolean;
  };
};

export type ValidateSsoEmailPayload = {
  email: string;
};

export type InviteUserPayload = {
  requestPayload: InviteUsersRequestPayload;
};

export enum UserRequestType {
  SUBMIT_TERMS_AND_CONDITIONS = 'Submit User terms and conditions',
}

const usersAdapter = createEntityAdapter<User>({
  // redundant, but makes obvious the key used for fetching records
  selectId: (user: User) => user.id,
});

//TODO: remove userListItems from UsersState, once one version of list of users is chosen
const userListAdapter = createEntityAdapter<User>({
  selectId: (user: User) => user.id,
});

// Union type of both key types
interface UsersState {
  selectedIndices: number[];
  selectedReverseMap: { [key: string]: number };
  operations: {
    [key in BDRequestType | UserRequestType]?: OperationStatus;
  };
  sessionConfigs: {
    [k in UserSessionViewType]?: UserSessionConfigType[k];
  };
  userListItems: EntityState<User>;
}

const initialState = usersAdapter.getInitialState<UsersState>({
  selectedIndices: [],
  selectedReverseMap: {},
  operations: {},
  sessionConfigs: {},
  userListItems: userListAdapter.getInitialState(),
});

const CONFIG = DEFAULT_API_CONFIG;

export const getUsers = makeThunk(
  UsersActionType.GET_USERS,
  makeGetPayloadCreator<ApiResponse<User[]>, undefined | UserParams>({
    url: `${globalThis.appConfig.apiBaseUrl}/users/${API_VERSION_DEFAULTS.default}`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(CONFIG, state.profile.currentLocale),
    argAdapter: (params) => {
      if (params) {
        return {
          requestParams: {
            organizationId: params.organizationsId || '',
            fleetId: params.fleetsId || '',
          },
        };
      }
      return {};
    },
    responseAdapter: (rawResponse: unknown) => {
      if (
        rawResponse &&
        typeof rawResponse === 'object' &&
        Object.hasOwn(rawResponse, '__bd')
      ) {
        const apiResponse = rawResponse as ApiResponse<unknown>;
        return {
          ...apiResponse,
          result: Array.isArray(apiResponse.result)
            ? apiResponse.result
                .filter(
                  (data): data is ProfileResponse =>
                    !!data && typeof data === 'object'
                )
                .map(mapApiResponseToUser)
            : [],
        };
      }
      throw new BDError('Unexpected user list response', { data: rawResponse });
    },
  })
);

// TODO: consolidate getUsers and getUserList once all consuming components use continuation token
export const getUserList = makeThunk(
  UsersActionType.GET_USER_LIST,
  makeGetPayloadCreator<
    ApiResponse<PagedResultWithErrors<User>>,
    UserListParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/users/${API_VERSION_DEFAULTS.userListAPIv2}`,
    axiosOptions: ({ continuationToken }, state) => {
      const commonHeaders = addAcceptLanguageHeader(
        CONFIG,
        state.profile.currentLocale
      );
      return continuationToken
        ? addHeader(
            commonHeaders,
            CS_CONTINUATION_TOKEN_HEADER_NAME,
            continuationToken
          )
        : commonHeaders;
    },

    argAdapter: (params) => {
      if (params) {
        const queryParams = buildUserListQueryParams({
          search: params.searchCriteria,
          filter: params.filterCriteria,
          orgId: params.organizationsId,
        });
        //Default sort order with below is by creation date in descending order, (i.e. newest first)
        return {
          requestParams: {
            ...queryParams,
            sortBy: 'CREATION_DATE',
            sortOrder: 'DESC',
            size: params.rowsPerPage?.toString() || '10',
          },
        };
      }
      return {};
    },
    responseAdapter: (rawResponse: unknown | ApiResponse<unknown>) => {
      if (rawResponse && hasApiResult<PagedResult<User>>(rawResponse)) {
        const { items, total_items, continuation_token } = rawResponse.result;
        return {
          ...rawResponse,
          result: {
            total_items,
            continuation_token,
            items: items.length
              ? items
                  .filter((data) => data && typeof data === 'object')
                  .map((data) => {
                    return mapUserRoleAndInvite(data);
                  })
              : [],
          },
        };
      }
      throw new BDError('Unexpected user list response', { data: rawResponse });
    },
  })
);
export const getUserDetails = makeThunk(
  'users/fetchDetails',
  makeGetPayloadCreator<
    ApiResponse<User>,
    Required<Pick<UserRouteParams, 'usersId'>>
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/users/${API_VERSION_DEFAULTS.default}/:usersId`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(CONFIG, state.profile.currentLocale),
    argAdapter: ({ usersId }) => ({
      requestParams: { usersId },
    }),
    responseAdapter: profileResponseAdapter,
  })
);

export const addUser = makeThunk(
  'users/addNewUser',
  makePostPayloadCreator<ApiResponse<User>, AddUserFormPayload>({
    url: `${globalThis.appConfig.apiBaseUrl}/users/${API_VERSION_DEFAULTS.addUserAPI}/admin`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(CONFIG, state.profile.currentLocale),
    argAdapter: (payload) => ({
      requestBody: mapUserFormToApiRequest(payload),
    }),
    responseAdapter: profileResponseAdapter,
  })
);

export const submitUserTCs = makeThunk(
  'users/submitUserTCs',
  makePostPayloadCreator<ApiResponse<User>, ActivateUserPayload>({
    url: `${globalThis.appConfig.apiBaseUrl}/users/${API_VERSION_DEFAULTS.acceptInviteAPI}/invite/accept`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(CONFIG, state.profile.currentLocale),
    argAdapter: (payload) => ({
      requestBody: payload.formFields,
    }),
  })
);

export const updateUser = makeThunk(
  'users/updateUser',
  makePutPayloadCreator<
    ApiResponse<User>,
    UpdateUserFormPayload & { entity?: Partial<User> }
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/users/${API_VERSION_DEFAULTS.default}/:usersId`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(CONFIG, state.profile.currentLocale),
    argAdapter: (payload) => ({
      requestParams: {
        usersId: payload.params.usersId,
      },
      requestBody: mapUserFormToApiRequest(payload, payload.entity),
    }),
    responseAdapter: profileResponseAdapter,
  })
);

export const deleteUser = makeThunk(
  'users/deleteUser',
  makeDeletePayloadCreator<
    ApiResponse,
    Required<Pick<UserRouteParams, 'usersId'>>
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/users/${API_VERSION_DEFAULTS.default}/:usersId`,
    argAdapter: ({ usersId }) => ({
      requestParams: { usersId },
    }),
  })
);

export const validateSsoEmail = makeThunk(
  'users/validateSsoEmail',
  makePostPayloadCreator<ApiResponse, ValidateSsoEmailPayload>({
    url: `${globalThis.appConfig.apiBaseUrl}/users/sso/${API_VERSION_DEFAULTS.validateSsoEmailAPI}/validateDomain`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(CONFIG, state.profile.currentLocale),
    argAdapter: (payload) => ({
      requestBody: { email: payload.email },
    }),
    skipAuth: true,
  })
);

//10/21/2024 - current POST format for Invite User
export const inviteUsers = makeThunk(
  'users/invite',
  makePostPayloadCreator<
    ApiResponse<InviteUsersResponsePayload>,
    InviteUserPayload
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/users/${API_VERSION_DEFAULTS.inviteAPI}/invites`,
    axiosOptions: (_payload, state) =>
      addAcceptLanguageHeader(
        addOrganizationIdHeader(
          CONFIG,
          state.profile.actingProfile?.organization?.id
        ),
        state.profile.currentLocale
      ),
    argAdapter: (payload) => ({
      requestBody: payload.requestPayload,
    }),
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (response && hasApiResult<InviteUsersResponsePayload>(response)) {
        return {
          ...response,
          result: response.result,
        };
      }
      throw new BDError('Unexpected Invite User response', {
        data: response,
      });
    },
  })
);

export const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setSelectedIndices: (state, action: PayloadAction<number[]>) => {
      state.selectedIndices = action.payload;

      // map associating each selected user id with its index in the selectedIndices array
      // used to speed up removal of selected users in assign dialog
      state.selectedReverseMap = action.payload.reduce(
        (reverseMap, selectedIndex, arrayIndex) => {
          if (state.ids.length > selectedIndex) {
            reverseMap[state.ids[selectedIndex]] = arrayIndex;
          }
          return reverseMap;
        },
        {} as { [key: string]: number }
      );
    },
    setUserSessionConfig: (
      state,
      action: PayloadAction<UserSessionConfigType>
    ) => {
      const mergeSelectedFilters = (_existing: string[], incoming: string[]) =>
        incoming;
      Object.keys(action.payload).forEach((key) => {
        const scope = key as UserSessionViewType;
        const payload = action.payload[scope] || {};
        Object.keys(payload).forEach((id) => {
          state.sessionConfigs = merge(
            state.sessionConfigs,
            {
              [scope]: {
                [id]: payload[id] && {
                  ...payload[id],
                },
              },
            },
            {
              customMerge: (key) => {
                if (Object.values(UserListFilterCriteriaKeys).includes(key)) {
                  return mergeSelectedFilters;
                }
              },
            }
          );
        });
      });
    },
    setUserListItems: (state, action: PayloadAction<User[]>) => {
      userListAdapter.setAll(state.userListItems, action.payload);
    },
    resetUserListContinuationCache: (
      state,
      action: PayloadAction<{
        viewType: UserSessionViewType;
        sessionId: string;
      }>
    ) => {
      const { viewType, sessionId } = action.payload;
      state.sessionConfigs = merge(state.sessionConfigs, {
        [viewType]: {
          [sessionId]: {
            continuationToken: null,
            continuationCache: undefined,
          },
        },
      });
    },
  },
  extraReducers: (builder) => {
    // user list
    builder.addCase(getUsers.pending, (state, action) => {
      state.operations[BDRequestType.GET_ALL] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
      state.selectedIndices = [];
      usersAdapter.removeMany(state, state.ids); // ensure no records when fetching

      if (action.meta.arg?.sessionId) {
        const safeSessionId = '' + action.meta.arg.sessionId;
        state.sessionConfigs = merge(
          state.sessionConfigs,
          {
            [UserSessionViewType.USER_LIST_VIEW]: {
              [safeSessionId]: {
                operationStatus: {
                  status: BDRequestStatus.PENDING,
                },
              },
            },
          },
          {
            arrayMerge: (_, sourceArray) => sourceArray,
          }
        );
      }
    });
    builder.addCase(getUsers.fulfilled, (state, action) => {
      state.operations[BDRequestType.GET_ALL] = {
        status: BDRequestStatus.SUCCEEDED,
      };
      usersAdapter.upsertMany(state, action.payload.result);
      if (action.meta.arg?.sessionId) {
        const safeSessionId = '' + action.meta.arg.sessionId;
        state.sessionConfigs = merge(
          state.sessionConfigs,
          {
            [UserSessionViewType.USER_LIST_VIEW]: {
              [safeSessionId]: {
                count: action.payload.result.length || 0,
                operationStatus: {
                  status: BDRequestStatus.SUCCEEDED,
                },
              },
            },
          },
          {
            arrayMerge: (_, sourceArray) => sourceArray,
          }
        );
      }
    });
    builder.addCase(getUsers.rejected, (state, action) => {
      state.operations[BDRequestType.GET_ALL] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.GET_ALL,
            severity: SeverityLevel.ERROR,
          },
        ],
      };

      if (action.meta.arg?.sessionId) {
        const safeSessionId = '' + action.meta.arg.sessionId;
        state.sessionConfigs = merge(
          state.sessionConfigs,
          {
            [UserSessionViewType.USER_LIST_VIEW]: {
              [safeSessionId]: {
                operationStatus: {
                  status: BDRequestStatus.FAILED,
                },
              },
            },
          },
          {
            arrayMerge: (_, sourceArray) => sourceArray,
          }
        );
      }
    });

    // user details
    builder.addCase(getUserDetails.pending, (state) => {
      state.operations[BDRequestType.GET_BY_ID] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });
    builder.addCase(getUserDetails.fulfilled, (state, action) => {
      state.operations[BDRequestType.GET_BY_ID] = {
        status: BDRequestStatus.SUCCEEDED,
      };
      usersAdapter.upsertOne(state, action.payload.result);
    });
    builder.addCase(getUserDetails.rejected, (state, action) => {
      state.operations[BDRequestType.GET_BY_ID] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.GET_BY_ID,
          },
        ],
      };
    });

    // add user
    builder.addCase(addUser.pending, (state) => {
      state.operations[BDRequestType.ADD] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });
    builder.addCase(addUser.fulfilled, (state) => {
      state.operations[BDRequestType.ADD] = {
        status: BDRequestStatus.SUCCEEDED,
      };
    });
    builder.addCase(addUser.rejected, (state, action) => {
      state.operations[BDRequestType.ADD] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.ADD,
          },
        ],
      };
    });

    // submit user t&c
    builder.addCase(submitUserTCs.pending, (state) => {
      state.operations[UserRequestType.SUBMIT_TERMS_AND_CONDITIONS] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });
    builder.addCase(submitUserTCs.fulfilled, (state) => {
      state.operations[UserRequestType.SUBMIT_TERMS_AND_CONDITIONS] = {
        status: BDRequestStatus.SUCCEEDED,
      };
    });
    builder.addCase(submitUserTCs.rejected, (state, action) => {
      state.operations[UserRequestType.SUBMIT_TERMS_AND_CONDITIONS] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.UPDATE,
          },
        ],
      };
    });

    // update user
    builder.addCase(updateUser.pending, (state) => {
      state.operations[BDRequestType.UPDATE] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });
    builder.addCase(updateUser.fulfilled, (state, action) => {
      state.operations[BDRequestType.UPDATE] = {
        status: BDRequestStatus.SUCCEEDED,
      };
      usersAdapter.upsertOne(state, action.payload.result);
    });
    builder.addCase(updateUser.rejected, (state, action) => {
      state.operations[BDRequestType.UPDATE] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.UPDATE,
          },
        ],
      };
    });

    // delete user
    builder.addCase(deleteUser.pending, (state) => {
      state.operations[BDRequestType.DELETE] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });
    builder.addCase(deleteUser.fulfilled, (state) => {
      state.operations[BDRequestType.DELETE] = {
        status: BDRequestStatus.SUCCEEDED,
      };
    });
    builder.addCase(deleteUser.rejected, (state, action) => {
      state.operations[BDRequestType.DELETE] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.DELETE,
          },
        ],
      };
    });

    //validate Email domain for SSO
    builder.addCase(validateSsoEmail.pending, (state) => {
      state.operations[BDRequestType.VALIDATE_SSO_EMAIL] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });
    builder.addCase(validateSsoEmail.fulfilled, (state) => {
      state.operations[BDRequestType.VALIDATE_SSO_EMAIL] = {
        status: BDRequestStatus.SUCCEEDED,
      };
    });
    builder.addCase(validateSsoEmail.rejected, (state, action) => {
      state.operations[BDRequestType.VALIDATE_SSO_EMAIL] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.VALIDATE_SSO_EMAIL,
          },
        ],
      };
    });

    //Invite Users for User Boarding - V0.5
    builder.addCase(inviteUsers.pending, (state) => {
      state.operations[BDRequestType.ADD] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });
    builder.addCase(inviteUsers.fulfilled, (state) => {
      state.operations[BDRequestType.ADD] = {
        status: BDRequestStatus.SUCCEEDED,
      };
    });
    builder.addCase(inviteUsers.rejected, (state, action) => {
      state.operations[BDRequestType.ADD] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.ADD,
          },
        ],
      };
    });

    // user list V2
    builder.addCase(getUserList.pending, (state, action) => {
      const { sessionId } = action.meta.arg;
      state.operations[BDRequestType.GET_ALL] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
      state.selectedIndices = [];
      userListAdapter.removeMany(state.userListItems, state.ids); // ensure no records when fetching
      if (sessionId) {
        const validSessionId = '' + sessionId;
        state.sessionConfigs = merge(
          state.sessionConfigs,
          {
            [UserSessionViewType.USER_LIST_VIEW]: {
              [validSessionId]: {
                operationStatus: {
                  status: BDRequestStatus.PENDING,
                },
              },
            },
          },
          {
            arrayMerge: (_, sourceArray) => sourceArray,
          }
        );
      }
    });
    builder.addCase(getUserList.fulfilled, (state, action) => {
      const { items, total_items, continuation_token } = action.payload.result;
      const { page, sessionId } = action.meta.arg;
      const existingConfigs =
        state.sessionConfigs[UserSessionViewType.USER_LIST_VIEW] || {};
      state.operations[BDRequestType.GET_ALL] = {
        status: BDRequestStatus.SUCCEEDED,
      };
      userListAdapter.setAll(state.userListItems, items);
      if (sessionId) {
        const validSessionId = '' + sessionId;
        state.sessionConfigs = merge(
          state.sessionConfigs,
          {
            [UserSessionViewType.USER_LIST_VIEW]: {
              [validSessionId]: {
                count: total_items || 0,
                continuationCache: {
                  ...existingConfigs.continuationCache,
                  [Number(page)]: {
                    data: items,
                    continuationToken: continuation_token ?? null,
                  },
                },
                operationStatus: {
                  status: BDRequestStatus.SUCCEEDED,
                },
              },
            },
          },
          {
            arrayMerge: (_, sourceArray) => sourceArray,
          }
        );
      }
    });
    builder.addCase(getUserList.rejected, (state, action) => {
      const { sessionId } = action.meta.arg;
      state.operations[BDRequestType.GET_ALL] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.GET_ALL,
            severity: SeverityLevel.ERROR,
          },
        ],
      };

      if (sessionId) {
        const validSessionId = '' + sessionId;
        state.sessionConfigs = merge(
          state.sessionConfigs,
          {
            [UserSessionViewType.USER_LIST_VIEW]: {
              [validSessionId]: {
                operationStatus: {
                  status: BDRequestStatus.FAILED,
                },
              },
            },
          },
          {
            arrayMerge: (_, sourceArray) => sourceArray,
          }
        );
      }
    });

    // reset state when persist store is purged on logout
    builder.addCase(PURGE, (_state) => {
      _state = initialState;
    });
  },
});

export const {
  setSelectedIndices,
  setUserSessionConfig,
  setUserListItems,
  resetUserListContinuationCache,
} = usersSlice.actions;

export const {
  selectAll: selectAllUsers,
  selectById: selectUserById,
  selectIds: selectUserIds,
} = usersAdapter.getSelectors<RootState>((state: RootState) => state.users);

export const { selectAll: selectAllUserListItems } =
  userListAdapter.getSelectors<RootState>(
    (state: RootState) => state.users.userListItems
  );

export const selectSelectedUsers = (state: RootState): User[] => {
  return state.users.selectedIndices.reduce((users, index) => {
    if (index < state.users.ids.length) {
      const user = selectUserById(state, state.users.ids[index]);
      if (user) {
        users.push(user);
      }
    }
    return users;
  }, [] as User[]);
};

export const usersReducer = usersSlice.reducer;
